import { animating } from '@site-module/util.ts';
import { Theme } from '@site-module/theme.ts';

/**
 * This enables open/close toggling of various menus in the header part:
 * - search
 * - language selection
 * - user menu
 * - main menu
 * - etc.
 *
 * Menus have an id, and are typically initially closed using aria-hidden=true.
 * aria-hidden=false means the menu is opened.
 * This script allows for the menus to have animations during opening and closing.
 * For floating menus it also handles positioning of the menu relative to the controlling button.
 *
 * Menus have one or more buttons:
 * - the menu they control is referenced using aria-controls (the value of which is the id of the menu)
 * - aria-expanded reflects the opened (true) or closed (false) state of the menu
 *
 * Opening a menu updates all related buttons to the new aria-expanded state.
 * Closing the menu is supported via a button or the ESC key,
 * and moves focus back to the button that originally opened the menu.
 */
export default function initHeaderMenus(){
    // user and language menu popup handling and positioning
    let header = document.getElementById('header-v2');
    let menus = {}; // every button controls a menu, which we'll store here
    let timer;

    if (! header) { // order process header
        header = document.getElementById('order-header');
    }

    if (! header) { // there is no header, so whatever
        return;
    }

    const theme_toggle = document.querySelector('.theme-toggle > .switch');
    if (theme_toggle) {
        new Theme(theme_toggle);
    }

    // we always look for the menu belonging to a specific button,
    // not the other way around.
    // we try to have focus restored to a relevant button, unless the action
    // was triggered by an "unfocused" event like a click outside the menu.
    function closeMenu(toggle_button, refocus) {
        let id = toggle_button?.getAttribute('aria-controls');
        let menu = document.getElementById(id);

        if (!menu) {
            return;
        }

        if (menu.getAttribute('aria-hidden') === 'true') {
            // it's already closed, leave it alone
            return;
        }

        // update the menu itself
        menu.setAttribute('aria-hidden', 'true');
        animating(menu);

        // update all buttons controlling this menu
        document.querySelectorAll('[aria-controls="' + id + '"]').forEach(button => {
            button?.setAttribute('aria-expanded', 'false');
        });

        Object.keys(menus).forEach(i => {
            const m = menus[i];
            const is_parent = m?.menu?.querySelector('#' + menu.id);
            if (is_parent) {
                window.clearTimeout(timer);
                timer = window.setTimeout(function() {
                    if (m.menu) {
                        m.menu.style.height = null;
                    }
                }, 200);
            }
        });

        if (! toggle_button?.classList.contains('v1') && refocus) {
            toggle_button?.focus();
        }

        // a special extra behaviour for the hamburger
        if (toggle_button?.id === 'foldout-toggle') {
            header.classList.remove('menu-opened');
            document.body.classList.remove('menu-opened');
        }

        menu.dispatchEvent(new Event('menu-closed'));
    }

    function openMenu(toggle_button) {
        let id = toggle_button?.getAttribute('aria-controls');
        let menu = document.getElementById(id);

        window.clearTimeout(timer);

        Object.keys(menus).forEach(i => {
            const m = menus[i];
            let is_parent = m?.menu?.querySelector('#' + menu?.id);
            let is_child = menu?.querySelector('#' + m?.menu?.id);
            // close all but this menu, or parents of this menu, or children of this menu
            if (m.menu !== menu && ! is_parent && ! is_child) {
                closeMenu(m.button, false);
            }
            if (is_parent) {
                m.menu.style.height = null;
            }
        });

        window.requestAnimationFrame(function() {
            // update all buttons controlling the same thing to the new state
            document.querySelectorAll('[aria-controls="' + id + '"]').forEach(button => {
                button.setAttribute('aria-expanded', 'true');
            });
            // update state of the controlled element
            menu.setAttribute('aria-hidden', 'false');

            if (header.classList.contains('suckerfish-small') && menu.classList.contains('suckerfish-submenu')) {
                menu.style.left = toggle_button.getBoundingClientRect().left + 'px';
            }

            window.setTimeout(function() {
                let focusable = Array.from(menu.querySelectorAll('a[href], input[type="text"]'));
                focusable = focusable.filter(el => el.checkVisibility({visibilityProperty: true}));

                if (focusable[0]) {
                    focusable[0].focus();
                }
            }, 200);
        });

        // a special extra behaviour for the hamburger
        if (toggle_button.id === 'foldout-toggle') {
            header.classList.add('menu-opened');
            document.body.classList.add('menu-opened');
        }

        menu.dispatchEvent(new Event('menu-opened'));
    }

    function toggleMenu(toggle_button) {
        let id = toggle_button?.getAttribute('aria-controls');
        let menu = document.getElementById(id);

        if (menu.getAttribute('aria-hidden') === 'true') {
            openMenu(toggle_button);
        } else {
            closeMenu(toggle_button, true);
        }
    }

    // loop over all buttons to find the menus they control,
    // and hook up their click handlers
    header.querySelectorAll('[aria-controls]').forEach(button => {
        // some components have their own scripting
        if (['waitlistModal', 'cart'].includes(button.getAttribute('aria-controls'))) {
            return;
        }

        let id = button.getAttribute('aria-controls');
        let menu = document.getElementById(id);

        button.onclick = function(event) {
            event.preventDefault();
            event.stopPropagation();
            toggleMenu(button);
        };
        button.addEventListener('keyup', function(event) {
            event.preventDefault();
            if (button.classList.contains('submenu-toggle') && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                event.stopPropagation();
                toggleMenu(button);
            } else if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
                event.stopPropagation();
                closeMenu(button, true);
            }
        });

        if (! menus[id]) {
            menus[id] = {
                'menu': menu,
                'button': button
            };
        }
    });

    // loop over the menus to hook up the ESC key so it and clicks outside the menu close it
    Object.keys(menus).forEach(i => {
        const menu = menus[i];

        menu.menu?.addEventListener('click', (event) => {
            // stop clicks inside the popup from propagating to the body
            event.stopPropagation();
        });

        menu.menu?.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();
                const parent_list = document.activeElement.closest('li')?.parentElement.closest('li');
                let button;
                if (parent_list) {
                    button = parent_list.querySelector('a');
                }
                if (button && JSON.parse(button.getAttribute('aria-expanded'))) {
                    toggleMenu(button);
                } else {
                    closeMenu(menu.button, true);
                }
            }
        });

        document.body.addEventListener('click', () => {
            // clicks inside the menu should not propagate,
            // so if the body receives a click you've left the menu and we close it
            closeMenu(menu.button, false);
        });
    });
}
